import {
  Button,
  Checkbox,
  IconButton,
  SelectInput,
  SelectInputItem,
  Tag,
  TextField,
  Tooltip,
} from '@ads/components-react';
import {
  BanknotesIcon,
  CalculatorIcon,
  InformationCircleIcon,
  PlusIcon,
  ReceiptPercentIcon,
  TicketIcon,
  TrashIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Controller, type FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentSaveDiscountViewModel } from '@/shared/api/generated';
import { Alert } from '@/shared/ui/atoms';

import { usePlanEdit } from '../../../context/PlanEditContext';
import { usePlanCoupons } from '../../../hooks/usePlanCoupons';
import { useCourseFinancialPaymentMethods } from '../../../hooks/usePlanCreateLookups';
import type { PlanFormValues } from '../../../hooks/usePlanFormSchema';
import {
  useAvailableNumberParcels,
  useAvailablePaymentMethods,
  useDiscountTypesDropdown,
} from '../../../hooks/usePlanLookups';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';
import {
  formatCurrencyInput,
  formatPercentageInput,
  parseCurrencyInput,
  parsePercentageInput,
} from '../../../utils/maskedNumberInput';
import { PlanReadOnlyField } from '../../molecules/PlanReadOnlyField';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';
import { AddCouponModal } from '../AddCouponModal';
import { AddDiscountModal } from '../AddDiscountModal';

/** Grade de três colunas. */
function Row({ children }: { readonly children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">{children}</div>;
}

/** Bloco cinza que agrupa leituras calculadas, como no legado. */
function Block({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex flex-col gap-xxxs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-micro md:flex-row">
      {children}
    </div>
  );
}

/** Percentual formatado, com placeholder para ausência. */
function formatPercentage(value: number | null | undefined): string {
  return value === null || value === undefined ? EMPTY_VALUE : `${value}%`;
}

/**
 * Aba "Valores" do formulário de edição de plano.
 *
 * Duas assimetrias importantes estão materializadas aqui:
 *
 * 1. **Descontos são estado do formulário; cupons são escrita imediata.** Mexer
 *    em desconto só persiste no Salvar, enquanto aplicar ou remover cupom chama
 *    endpoint na hora e invalida o editor. A UI declara isso em texto para o
 *    operador não supor que as duas tabelas funcionam igual.
 * 2. **Os valores do resumo nunca são recalculados no cliente.** A fórmula é do
 *    backend, e há questão aberta sobre o cupom entrar no desconto total; um
 *    espelho errado faria o operador decidir sobre número que o backend
 *    contradiz.
 */
export function PlanValuesTab() {
  const { t } = useTranslation('individualPayment');
  const { mode, form, editor, financialPersonId, isReadOnly } = usePlanEdit();
  const { control, watch, setValue, formState, getFieldState } = form;

  const isCreate = mode === 'create';

  /**
   * Mensagem de validação traduzida de um campo.
   *
   * As mensagens do schema são **chaves de i18n**, não texto final — o schema é
   * derivado do gerado pelo Kubb e não tem acesso ao `t()`.
   *
   * Sem isto, reprovar um campo desta aba não produzia nenhum sinal na tela: o
   * `handleSubmit` barrava o envio e o botão parecia inerte. É o `formState` que
   * transforma a recusa em algo que o operador consegue ver e corrigir.
   */
  const errorOf = (name: FieldPath<PlanFormValues>): string | undefined => {
    const message = getFieldState(name, formState).error?.message;
    // biome-ignore lint/suspicious/noExplicitAny: a chave vem do schema, fora do union tipado de t().
    return typeof message === 'string' ? String(t(message as any)) : undefined;
  };

  /**
   * Na criação o plano financeiro do curso vem do formulário — é o operador quem
   * o escolhe no bloco de vínculo. Na edição ele chega pronto no editor.
   */
  const formCourseFinancialId = watch('courseFinancialId') as number | undefined;
  const courseFinancialId = isCreate
    ? (formCourseFinancialId ?? null)
    : (editor?.courseFinancialId ?? null);

  // Duas origens para a mesma pergunta: por contrato na edição, por plano do
  // curso na criação — onde o `financialPersonId` ainda não existe. Os dois hooks
  // são chamados sempre (regra dos hooks) e o desabilitado não dispara request.
  const { paymentMethods: contractPaymentMethods } = useAvailablePaymentMethods(
    isCreate ? null : financialPersonId,
  );
  const { paymentMethods: coursePaymentMethods, isLoading: isLoadingPaymentMethods } =
    useCourseFinancialPaymentMethods(isCreate ? courseFinancialId : null);
  const paymentMethods = isCreate ? coursePaymentMethods : contractPaymentMethods;

  /**
   * Forma de pagamento travada enquanto o plano do curso não for escolhido.
   *
   * As formas aceitas vêm de `course-financials/{id}/available-payment-methods`,
   * então sem o plano a query nem roda e o select abriria vazio — indistinguível
   * de "este plano não aceita nenhuma forma de pagamento".
   */
  const isPaymentMethodLocked = isCreate && courseFinancialId === null;

  /**
   * Plano escolhido, consulta concluída e mesmo assim nenhuma opção.
   *
   * Diferente do caso acima: aqui o backend respondeu que não há forma de
   * pagamento configurada para este plano do curso. Sem dizer isso, o operador
   * ficaria diante de um campo obrigatório que não abre, sem saber se é a tela
   * ou o cadastro do curso.
   */
  const hasNoPaymentMethod =
    isCreate && !isPaymentMethodLocked && !isLoadingPaymentMethods && paymentMethods.length === 0;

  const { discountTypes } = useDiscountTypesDropdown();
  // As opções de parcelamento variam com a forma de pagamento — cartão e boleto
  // não aceitam as mesmas divisões —, então o campo observa `paymentMethod`.
  const selectedPaymentMethod = watch('paymentMethod') as number | null | undefined;
  const { options: parcelOptions } = useAvailableNumberParcels(
    courseFinancialId,
    // `0` é o default do formulário na criação, não uma forma de pagamento: sem
    // escolha real o backend não tem como restringir a lista.
    selectedPaymentMethod ? selectedPaymentMethod : null,
    editor?.numberParcels,
  );
  const {
    applyCoupon,
    removeCoupon,
    isPending: isCouponPending,
  } = usePlanCoupons(financialPersonId);

  const [isDiscountModalOpen, setDiscountModalOpen] = useState(false);
  const [isCouponModalOpen, setCouponModalOpen] = useState(false);

  // O schema gerado é permissivo e o Zod infere `discounts` de forma opaca; a
  // forma real é a do `IndividualPaymentSaveDiscountViewModel`.
  const discounts = (watch('discounts') ?? []) as readonly IndividualPaymentSaveDiscountViewModel[];

  /**
   * Acrescenta o desconto ao formulário — sem endpoint.
   *
   * `discounts[]` viaja no corpo do `PUT` do plano, então a inclusão só existe
   * de fato quando o operador salva.
   */
  const addDiscount = (discount: IndividualPaymentSaveDiscountViewModel) => {
    setValue('discounts', [...discounts, discount] as PlanFormValues['discounts'], {
      shouldDirty: true,
    });
    setDiscountModalOpen(false);
  };

  /** Remove por índice, não por `id`: itens novos ainda não têm `id`. */
  const removeDiscount = (index: number) => {
    setValue(
      'discounts',
      discounts.filter((_, position) => position !== index) as PlanFormValues['discounts'],
      { shouldDirty: true },
    );
  };

  /**
   * Linhas da tabela de descontos.
   *
   * A fonte é o **formulário**, porque é ele que define o que será salvo. O
   * editor apenas enriquece: ele traz os campos que só existem na leitura — tipo
   * por extenso, conveniada, retroativo, aprovado — que o payload de escrita não
   * carrega. Itens sem correspondente no editor são inclusões pendentes.
   */
  const discountRows = discounts.map((item, index) => {
    const saved =
      item.id === undefined
        ? undefined
        : (editor?.discounts ?? []).find(entry => entry.id === item.id);

    return {
      index,
      id: item.id,
      isPending: saved === undefined,
      typeText:
        saved?.discountTypeText ??
        discountTypes.find(option => option.id === item.discountTypeId)?.label ??
        EMPTY_VALUE,
      percentage: item.percentage ?? saved?.percentage,
      description: item.description ?? saved?.description,
      associatedCompany: saved?.associatedCompany ?? null,
      retroactive: saved?.retroactive ?? false,
      approved: saved?.approved ?? false,
    };
  });
  const closedParcels = editor?.closedParcels ?? 0;
  const totalParcels = editor?.numberParcels ?? 0;
  const selectedParcels = watch('numberParcels') as number;
  /** Valor da matrícula — decide se a parcela de matrícula faz sentido. */
  const registrationValue = (watch('valueRegistration') as number | null | undefined) ?? 0;
  /**
   * A última opção devolvida pelo backend é "Outro".
   *
   * Ela **não é uma quantidade** — é o gatilho para digitar um número livre. Por
   * isso seu `id` nunca vai para o formulário: selecioná-la abre o campo e zera
   * o valor, em vez de gravar o id da opção como se fosse número de parcelas.
   */
  const otherOptionId = parcelOptions.length > 0 ? String(parcelOptions.at(-1)?.id) : null;

  /** As opções que são quantidades de verdade, sem a "Outro". */
  const parcelChoices = parcelOptions.slice(0, -1);

  /**
   * Um valor fora das quantidades padrão significa "Outro".
   *
   * Cobre os dois caminhos: quem acabou de escolher "Outro" e o plano já salvo
   * com uma quantidade combinada fora da lista — que precisa abrir já no modo
   * livre, senão o campo apareceria vazio.
   */
  const isCustomParcels =
    parcelChoices.length > 0 &&
    selectedParcels > 0 &&
    !parcelChoices.some(option => Number(option.id) === selectedParcels);

  const [wantsCustomParcels, setWantsCustomParcels] = useState(false);
  const showCustomParcels = wantsCustomParcels || isCustomParcels;

  /**
   * Parcelamento travado enquanto não há forma de pagamento escolhida.
   *
   * As divisões aceitas dependem dela — cartão e boleto não parcelam igual —,
   * então sem essa escolha o backend não tem como listar as opções e o campo
   * abriria vazio. Travar diz ao operador que falta um passo antes; um select
   * habilitado e sem opções pareceria um plano sem parcelamento disponível.
   *
   * Só vale na criação: na edição a forma de pagamento chega preenchida do
   * contrato, e a trava nunca teria efeito além de arriscar prender um plano
   * legado cujo valor o backend não devolveu.
   */
  const isParcelsLocked = isCreate && !selectedPaymentMethod;

  type NumericFieldName =
    | 'valueRegistration'
    | 'totalValue'
    | 'expirationDate'
    | 'discountPunctualPercentage';

  const numberField = (
    name: NumericFieldName,
    label: string,
    placeholder: string,
    options?: {
      readonly required?: boolean;
      readonly currency?: boolean;
      readonly percentage?: boolean;
      /**
       * Faixa aceita. A mensagem aparece **apenas** quando o valor sai dela, como
       * erro — texto de apoio permanente vira ruído num campo que quase sempre
       * está correto.
       */
      readonly range?: {
        readonly min: number;
        readonly max: number;
        readonly message: string;
      };
    },
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const raw = typeof field.value === 'number' ? field.value : null;

        /**
         * Num campo cuja faixa começa acima de zero, `0` é **ausência de valor**,
         * não um valor recusado.
         *
         * É como o dia de vencimento chega num plano em criação (`0` nos
         * defaults) e num plano legado sem o campo preenchido. Tratá-lo como
         * fora da faixa acendia o erro "deve estar entre 1 e 28" assim que a aba
         * abria, num campo que o operador nem tocou — a cobrança de obrigatório
         * é do submit, não da renderização.
         */
        const isEmpty =
          raw === null || (options?.range !== undefined && options.range.min > 0 && raw === 0);
        const numeric = isEmpty ? null : raw;

        const outOfRange =
          options?.range !== undefined &&
          numeric !== null &&
          (numeric < options.range.min || numeric > options.range.max);

        // A recusa do schema tem precedência sobre a faixa: ela é a razão pela
        // qual o envio foi barrado, e é o que o operador precisa resolver.
        const schemaError = errorOf(name);
        const helper =
          schemaError ?? (outOfRange && options?.range ? options.range.message : undefined);

        return (
          <TextField
            label={options?.required ? `${label} *` : label}
            size="md"
            fullWidth
            hasError={outOfRange || schemaError !== undefined}
            {...(helper === undefined ? {} : { helper })}
            // `fullWidth` estica o wrapper; o `w-full` no input garante que o campo
            // ocupe a coluna inteira do grid em vez da largura intrínseca.
            custom={{ root: 'w-full', input: 'w-full' }}
            placeholder={placeholder}
            disabled={isReadOnly}
            inputMode="numeric"
            value={
              options?.currency
                ? formatCurrencyInput(field.value as number | null)
                : options?.percentage
                  ? formatPercentageInput(field.value as number | null)
                  : isEmpty
                    ? ''
                    : String(numeric)
            }
            onChange={event => {
              if (options?.currency) {
                field.onChange(parseCurrencyInput(event.target.value));
                return;
              }
              if (options?.percentage) {
                field.onChange(parsePercentageInput(event.target.value));
                return;
              }
              const next = Number(event.target.value.replace(',', '.'));
              field.onChange(Number.isFinite(next) ? next : null);
            }}
          />
        );
      }}
    />
  );

  return (
    <>
      <PlanSectionCard title={t('planEdit.values.plan.title')} icon={BanknotesIcon}>
        <Row>
          {numberField(
            'valueRegistration',
            t('planEdit.values.plan.valueRegistration'),
            t('planEdit.placeholders.currency'),
            { currency: true },
          )}
          <Controller
            control={control}
            name="paymentMethod"
            render={({ field }) => (
              <SelectInput
                label={`${t('planEdit.values.plan.paymentMethod')} *`}
                size="md"
                placeholder={t('planEdit.placeholders.paymentMethod')}
                fullWidth
                disabled={isReadOnly || isPaymentMethodLocked}
                hasError={hasNoPaymentMethod || errorOf('paymentMethod') !== undefined}
                {...(errorOf('paymentMethod') !== undefined
                  ? { helper: errorOf('paymentMethod') as string }
                  : isPaymentMethodLocked
                    ? { helper: t('planCreate.values.paymentMethodNeedsPlan') }
                    : hasNoPaymentMethod
                      ? { helper: t('planCreate.values.noPaymentMethod') }
                      : {})}
                value={field.value ? String(field.value) : ''}
                onValueChange={value => field.onChange(Number(value))}
              >
                {paymentMethods.map(option => (
                  <SelectInputItem
                    custom={{ root: 'text-nowrap' }}
                    key={String(option.id)}
                    value={String(option.id)}
                  >
                    {option.label}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
          />
          {numberField(
            'expirationDate',
            t('planEdit.values.plan.expirationDate'),
            t('planEdit.placeholders.paymentDay'),
            { range: { min: 1, max: 28, message: t('planEdit.validation.expirationDateRange') } },
          )}
        </Row>

        <Row>
          {numberField(
            'totalValue',
            t('planEdit.values.plan.totalValue'),
            t('planEdit.placeholders.currency'),
            { required: true, currency: true },
          )}
          {numberField(
            'discountPunctualPercentage',
            t('planEdit.values.plan.punctualDiscount'),
            t('planEdit.placeholders.percentage'),
            { percentage: true },
          )}
          <div>
            <Controller
              control={control}
              name="numberParcels"
              render={({ field }) => (
                // Lado a lado: o campo livre é a continuação da escolha "Outros",
                // e empilhado ele lia como um campo independente do formulário.
                // `items-start` mantém os rótulos alinhados quando só um tem helper.
                <div className="flex items-start gap-nano">
                  <div className="flex-1">
                    <SelectInput
                      label={`${t('planEdit.values.plan.numberParcels')} *`}
                      size="md"
                      fullWidth
                      placeholder={t('planEdit.placeholders.numberParcels')}
                      disabled={isReadOnly || isParcelsLocked}
                      hasError={errorOf('numberParcels') !== undefined}
                      {...(errorOf('numberParcels') !== undefined
                        ? { helper: errorOf('numberParcels') as string }
                        : isParcelsLocked
                          ? { helper: t('planCreate.values.parcelsNeedPaymentMethod') }
                          : {})}
                      value={
                        showCustomParcels && otherOptionId !== null
                          ? otherOptionId
                          : field.value
                            ? String(field.value)
                            : ''
                      }
                      onValueChange={value => {
                        if (value === otherOptionId) {
                          // Zera para o operador digitar; o id da opção "Outro" não
                          // é uma quantidade e não pode virar `numberParcels`.
                          setWantsCustomParcels(true);
                          field.onChange(0);
                          return;
                        }
                        setWantsCustomParcels(false);
                        field.onChange(Number(value));
                      }}
                    >
                      {/* A lista vem inteira do backend, "Outro" incluída — o
                        rótulo dela é o que o backend mandar, não texto nosso. */}
                      {parcelOptions.map(option => (
                        <SelectInputItem
                          custom={{ root: 'text-nowrap' }}
                          key={String(option.id)}
                          value={String(option.id)}
                        >
                          {option.label}
                        </SelectInputItem>
                      ))}
                    </SelectInput>
                  </div>

                  {showCustomParcels ? (
                    <div className="flex-1">
                      <TextField
                        label={t('planEdit.values.plan.otherParcelsLabel')}
                        size="md"
                        fullWidth
                        custom={{ root: 'w-full', input: 'w-full' }}
                        inputMode="numeric"
                        maxLength={3}
                        // O contador do ADS ("0/3") aparece junto ao rótulo e
                        // roubava a linha dele, forçando a quebra em duas.
                        hideLengthCounter
                        placeholder={t('planEdit.placeholders.otherParcels')}
                        disabled={isReadOnly || isParcelsLocked}
                        value={field.value ? String(field.value) : ''}
                        onChange={event => {
                          const digits = event.target.value.replace(/\D/g, '');
                          field.onChange(digits === '' ? 0 : Number(digits));
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              )}
            />
          </div>
        </Row>

        {/*
          Somente leitura: `discountReferralAmount` existe apenas no
          `IndividualPaymentEditorViewModel`. O `IndividualPaymentSaveViewModel`
          não tem essa propriedade, então não há onde gravá-la — um input aqui
          prometeria uma edição que o `PUT` descartaria em silêncio.
        */}
        <Row>
          <PlanReadOnlyField
            label={t('planEdit.values.plan.referralDiscount')}
            value={formatCurrency(editor?.discountReferralAmount)}
          />
          <span />
          <span />
        </Row>

        {/*
          Só na criação e só com valor de matrícula — a mesma regra do legado
          (ver `PaymentPlanForm` em student-management). Editar um plano não
          recria a parcela de matrícula, e sem valor não há o que cobrar.
        */}
        {isCreate && registrationValue > 0 ? (
          <div className="flex flex-col gap-quark">
            <Controller
              control={control}
              name="generateEnrollmentParcel"
              render={({ field }) => (
                <Checkbox
                  checked={field.value === true}
                  size="sm"
                  disabled={isReadOnly}
                  label={t('planEdit.values.plan.generateEnrollmentParcel')}
                  onCheckedChange={checked => field.onChange(checked === true)}
                />
              )}
            />
            <span className="ts-paragraph-xxs text-ctx-content-placeholder">
              {t('planEdit.values.plan.generateEnrollmentParcelHint')}
            </span>
          </div>
        ) : null}

        {/* Não há mais alerta de "número recusado": com a opção "Outro", uma
            quantidade fora da lista é legítima. Quem valida quantidade livre é o
            endpoint `check-number-parcels`, ainda não integrado (tarefa 5.3). */}
        {closedParcels > 0 ? (
          <Alert
            type="warning"
            title={t('planEdit.values.closedWarning.title', {
              closed: closedParcels,
              total: totalParcels,
            })}
            description={t('planEdit.values.closedWarning.description')}
          />
        ) : null}
      </PlanSectionCard>

      <PlanSectionCard
        title={t('planEdit.values.discounts.title')}
        icon={ReceiptPercentIcon}
        headerSlot={
          <Button
            hierarchy="secondary"
            size="sm"
            leadingIcon={PlusIcon}
            disabled={isReadOnly}
            onClick={() => setDiscountModalOpen(true)}
          >
            {t('planEdit.values.discounts.add')}
          </Button>
        }
      >
        <span className="ts-paragraph-xxs text-ctx-content-placeholder">
          {t('planEdit.values.discounts.pendingSave')}
        </span>
        {discountRows.length > 0 ? (
          <div className="flex flex-col overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
            <div className="flex items-center gap-micro bg-ctx-layout-body px-micro py-nano">
              <span className="w-[12rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                {t('planEdit.values.discounts.type')}
              </span>
              <span className="w-[6rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                {t('planEdit.values.discounts.percentage')}
              </span>
              <span className="flex-1 ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                {t('planEdit.values.discounts.description')}
              </span>
              <span className="w-[11rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                {t('planEdit.values.discounts.company')}
              </span>
              <span className="w-[5rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                {t('planEdit.values.discounts.retroactive')}
              </span>
              <span className="w-[5rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                {t('planEdit.values.discounts.approved')}
              </span>
              <span className="w-xs" />
            </div>
            {discountRows.map(row => (
              <div
                key={row.id ?? `pending-${row.index}`}
                className="flex items-center gap-micro border-t-hairline border-ctx-layout-border px-micro py-micro"
              >
                <span className="flex w-[12rem] items-center gap-quark">
                  <span className="ts-paragraph-xs text-ctx-content-title">{row.typeText}</span>
                  {row.isPending ? (
                    <Tag
                      label={t('planEdit.values.discounts.newTag')}
                      size="sm"
                      status="highlight"
                      custom={{ text: 'text-nowrap' }}
                    />
                  ) : null}
                </span>
                <span className="w-[6rem] ts-paragraph-xs text-ctx-content-title">
                  {formatPercentage(row.percentage)}
                </span>
                <span className="flex-1 ts-paragraph-xs text-ctx-content-base">
                  {row.description ?? EMPTY_VALUE}
                </span>
                <span className="w-[11rem] ts-paragraph-xs text-ctx-content-base">
                  {row.associatedCompany ?? EMPTY_VALUE}
                </span>
                <span className="w-[5rem] ts-paragraph-xs text-ctx-content-base">
                  {row.retroactive
                    ? t('planEdit.values.discounts.yes')
                    : t('planEdit.values.discounts.no')}
                </span>
                <span className="w-[5rem]">
                  <Tag
                    label={
                      row.approved
                        ? t('planEdit.values.discounts.approvedTag')
                        : t('planEdit.values.discounts.pendingTag')
                    }
                    size="sm"
                    status={row.approved ? 'positive' : 'helper'}
                    custom={{ text: 'text-nowrap' }}
                  />
                </span>
                <span className="w-xs">
                  <IconButton
                    icon={TrashIcon}
                    hierarchy="tertiary"
                    size="sm"
                    disabled={isReadOnly}
                    ariaLabel={t('planEdit.values.discounts.remove')}
                    onClick={() => removeDiscount(row.index)}
                  />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span className="rounded-sm border-hairline border-ctx-layout-border px-micro py-xs text-center ts-paragraph-xs text-ctx-content-placeholder">
            {t('planEdit.values.discounts.empty')}
          </span>
        )}
      </PlanSectionCard>

      {/* Cupom exige um contrato já criado: `POST /financial-people/{id}/coupons`
          tem o id no path, e ele só existe depois do `POST` de criação. A seção
          aparece na edição, onde o operador completa o plano recém-criado. */}
      {isCreate ? null : (
        <PlanSectionCard
          title={t('planEdit.values.coupons.title')}
          icon={TicketIcon}
          headerSlot={
            <Button
              hierarchy="secondary"
              size="sm"
              leadingIcon={PlusIcon}
              disabled={isReadOnly || isCouponPending}
              onClick={() => setCouponModalOpen(true)}
            >
              {t('planEdit.values.coupons.add')}
            </Button>
          }
        >
          <span className="ts-paragraph-xxs text-ctx-content-placeholder">
            {t('planEdit.values.coupons.immediate')}
          </span>
          {editor?.couponsHistory && editor.couponsHistory.length > 0 ? (
            <div className="flex flex-col overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
              {/* Larguras espelham as da linha de dados: sem isso o cabeçalho
                desalinha das células assim que um texto cresce. */}
              <div className="flex items-center gap-micro border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-micro py-nano">
                <span className="w-[10rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                  {t('planEdit.values.coupons.name')}
                </span>
                <span className="w-[8rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                  {t('planEdit.values.coupons.discount')}
                </span>
                <span className="flex-1 ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                  {t('planEdit.values.coupons.creationJustification')}
                </span>
                <span className="w-[12rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                  {t('planEdit.values.coupons.createdBy')}
                </span>
                <span className="w-[12rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                  {t('planEdit.values.coupons.deleteJustification')}
                </span>
                <span className="w-[5rem] ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                  {t('planEdit.values.coupons.active')}
                </span>
                <span className="w-xs" />
              </div>
              {editor.couponsHistory.map((coupon, index) => (
                <div
                  key={coupon.id ?? `coupon-${index}`}
                  className="flex items-center gap-micro border-b-hairline border-ctx-layout-border px-micro py-micro last:border-b-0"
                >
                  <span className="w-[10rem] ts-paragraph-xxs text-ctx-content-base">
                    {coupon.couponName ?? EMPTY_VALUE}
                  </span>
                  <span className="w-[8rem] ts-paragraph-xxs text-ctx-content-base">
                    {coupon.discountValue != null
                      ? formatCurrency(coupon.discountValue)
                      : formatPercentage(coupon.discountPercentage)}
                  </span>
                  <span className="flex-1 ts-paragraph-xxs text-ctx-content-base">
                    {coupon.creationJustification ?? EMPTY_VALUE}
                  </span>
                  <span className="w-[12rem] ts-paragraph-xxs text-ctx-content-placeholder">
                    {coupon.userNameCreator ?? EMPTY_VALUE}
                  </span>
                  <span className="w-[12rem] ts-paragraph-xxs text-ctx-content-placeholder">
                    {coupon.deleteJustification ?? EMPTY_VALUE}
                  </span>
                  <span className="w-[5rem]">
                    <Tag
                      label={
                        coupon.active
                          ? t('planEdit.values.coupons.activeTag')
                          : t('planEdit.values.coupons.inactiveTag')
                      }
                      size="sm"
                      status={coupon.active ? 'positive' : 'default'}
                      custom={{ text: 'text-nowrap' }}
                    />
                  </span>
                  <span className="w-xs">
                    {coupon.active && coupon.id !== undefined ? (
                      <IconButton
                        icon={TrashIcon}
                        hierarchy="tertiary"
                        size="sm"
                        isLoading={isCouponPending}
                        disabled={isReadOnly || isCouponPending}
                        ariaLabel={t('planEdit.values.coupons.remove')}
                        onClick={() =>
                          removeCoupon({
                            couponId: coupon.id as number,
                            justification: t('planEdit.values.coupons.remove'),
                          })
                        }
                      />
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="rounded-sm border-hairline border-ctx-layout-border px-micro py-xs text-center ts-paragraph-xs text-ctx-content-placeholder">
              {t('planEdit.values.coupons.empty')}
            </span>
          )}
        </PlanSectionCard>
      )}

      {/* Os valores do resumo são calculados pelo backend e vivem apenas no
          `IndividualPaymentEditorViewModel` — a resposta do editor, que só existe
          para um contrato já criado. Não há endpoint de prévia, e a tela
          deliberadamente não recomputa a fórmula. Na criação o card só teria
          traços, o que o operador leria como cálculo quebrado em vez de "ainda
          não há o que calcular". */}
      {isCreate ? null : (
        <PlanSectionCard
          title={t('planEdit.values.summary.title')}
          icon={CalculatorIcon}
          badge={t('planEdit.values.summary.readOnly')}
        >
          <Block>
            <PlanReadOnlyField
              label={t('planEdit.values.summary.parcelValue')}
              value={formatCurrency(editor?.readMonthValue)}
              emphasis
            />
          </Block>
          <Block>
            <PlanReadOnlyField
              label={t('planEdit.values.summary.totalDiscountPercentage')}
              value={formatPercentage(editor?.readTotalDiscountPercentage)}
              info={t('planEdit.values.summary.totalDiscountPercentageTooltip')}
            />
            <PlanReadOnlyField
              label={t('planEdit.values.summary.totalDiscount')}
              value={formatCurrency(editor?.readTotalDiscount)}
            />
            <PlanReadOnlyField
              label={t('planEdit.values.summary.parcelValueWithDiscount')}
              value={formatCurrency(editor?.readMonthValueWithDiscount)}
            />
          </Block>
          <Block>
            <PlanReadOnlyField
              label={t('planEdit.values.summary.totalDiscountPunctualPercentage')}
              value={formatPercentage(editor?.readTotalDiscountPercentageWithPunctual)}
              info={t('planEdit.values.summary.totalDiscountPunctualPercentageTooltip')}
            />
            <PlanReadOnlyField
              label={t('planEdit.values.summary.totalDiscountPunctual')}
              value={formatCurrency(editor?.readTotalDiscountWithPunctual)}
            />
            <PlanReadOnlyField
              label={t('planEdit.values.summary.parcelValueWithDiscountPunctual')}
              value={formatCurrency(editor?.readMonthValueWithDiscountAndPunctual)}
            />
          </Block>
          <span className="ts-paragraph-xxs !font-semibold text-ctx-content-title">
            {t('planEdit.values.summary.totalsTitle')}
          </span>
          <Block>
            <PlanReadOnlyField
              label={t('planEdit.values.summary.totalWithDiscount')}
              value={formatCurrency(editor?.readTotalValueWithDiscount)}
            />
            <PlanReadOnlyField
              label={t('planEdit.values.summary.totalWithDiscountPunctual')}
              value={formatCurrency(editor?.readTotalValueWithDiscountAndPunctual)}
              info={t('planEdit.values.summary.totalWithDiscountPunctualTooltip')}
            />
            <span className="flex-1" />
          </Block>
        </PlanSectionCard>
      )}

      {/* Nada aqui existe antes da criação: não há pagamento realizado, e recriar
          parcelas não faz sentido num plano cujas parcelas o próprio `POST` gera. */}
      {isCreate ? null : (
        <PlanSectionCard
          title={t('planEdit.values.payment.title')}
          icon={WalletIcon}
          badge={t('planEdit.values.summary.readOnly')}
        >
          <Block>
            <PlanReadOnlyField
              label={t('planEdit.values.payment.totalPaid')}
              value={formatCurrency(editor?.totalPaidWithoutRetroactive)}
            />
            <PlanReadOnlyField
              label={t('planEdit.values.payment.paidParcels')}
              value={t('planEdit.contract.closedOf', {
                closed: closedParcels,
                total: totalParcels,
              })}
            />
            <PlanReadOnlyField
              label={t('planEdit.values.payment.valueTransfer')}
              value={formatCurrency(editor?.valueTransfer)}
            />
          </Block>

          <div className="flex flex-col gap-quark">
            <span className="flex items-center gap-nano">
              <Controller
                control={control}
                name="updatePlanParcels"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value === true}
                    size="sm"
                    disabled={isReadOnly}
                    label={t('planEdit.values.updateParcels')}
                    onCheckedChange={checked => field.onChange(checked === true)}
                  />
                )}
              />
              <Tooltip title={t('planEdit.values.updateParcelsTooltip')}>
                <InformationCircleIcon className="size-mini shrink-0 text-ctx-content-placeholder" />
              </Tooltip>
            </span>
          </div>
        </PlanSectionCard>
      )}

      <AddDiscountModal
        open={isDiscountModalOpen}
        onAdd={addDiscount}
        onClose={() => setDiscountModalOpen(false)}
      />

      {/* Sem contrato não há endpoint de cupom a chamar — o modal nem é montado. */}
      {isCreate ? null : (
        <AddCouponModal
          open={isCouponModalOpen}
          isPending={isCouponPending}
          // Cupom persiste na hora: fecha só quando o endpoint confirma.
          onApply={input => applyCoupon(input, { onSuccess: () => setCouponModalOpen(false) })}
          onClose={() => setCouponModalOpen(false)}
        />
      )}
    </>
  );
}
